import { Result } from "@/api/types";
import useUrlStateNumber from "@/hooks/useUrlStateNumber";
import { UserTypeContext } from "@/providers/UserTypeProvider";
import { YearContext } from "@/providers/YearProvider";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";

export interface DateRange {
  min_date: string;
  max_date: string;
}

export interface GetDateRange extends Result {
  result: DateRange;
}

export const dateFormat = "YYYY-MM-DD";

export function useMinMaxDateRange() {
  const [minDate, setMinDate] = useState<dayjs.Dayjs>();
  const [maxDate, setMaxDate] = useState<dayjs.Dayjs>();

  const { data } = useQuery({
    queryKey: ["dashboard", "date_range"],
    queryFn: () => axios.get<GetDateRange>("dashboard/date_range").then((data) => data.data.result),
  });

  useEffect(() => {
    setMinDate(data?.min_date ? dayjs(data?.min_date, dateFormat).startOf("month") : undefined);
    setMaxDate(data?.max_date ? dayjs(data?.max_date, dateFormat).endOf("month") : undefined);
  }, [data]);

  return { minDate, maxDate };
}

export function useYearRange(enabled: boolean = true) {
  return useQuery({
    queryKey: ["dashboard", "year_range"],
    enabled,
    queryFn: () =>
      axios.get<Result<number[]>>("dashboard/year_range").then((data) => data.data.result),
  });
}

export function useDateRange() {
  const { year } = useDashboardYear();

  const [startDate, setStartDate] = useState<dayjs.Dayjs>();
  const [endDate, setEndDate] = useState<dayjs.Dayjs>();

  useEffect(() => {
    if (year) {
      setStartDate(dayjs(year + "-01-01", dateFormat));
      setEndDate(dayjs(year + "-12-31", dateFormat));
    }
  }, [year]);

  return { startDate, endDate };
}

export function useDashboardYear() {
  const { userType } = useContext(UserTypeContext);
  const { year, updateYear } = useContext(YearContext);
  const [urlYear, setUrlYear] = useUrlStateNumber("year", undefined, true);

  if (userType !== "internal") {
    // if user is external (supplier), return year from url
    return { year: urlYear, updateYear: setUrlYear };
  } else {
    return { year, updateYear };
  }
}
